import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/app/context/AuthContext';
import { API_ENDPOINTS } from '@/config/api';

export type AvatarMode = 'ai' | 'anime' | 'plain' | 'manga' | 'runes';

const DEFAULT_MODE: AvatarMode = 'ai';
const VALID_MODES: AvatarMode[] = ['ai', 'anime', 'plain', 'manga', 'runes'];
const STORAGE_KEY = 'saxa_avatar_mode';

function readLocalMode(): AvatarMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_MODES.includes(stored as AvatarMode)) return stored as AvatarMode;
  } catch { /* SSR / private browsing */ }
  return DEFAULT_MODE;
}

/**
 * Manages Saxa's avatar content mode.
 * - Anonymous visitors: mode stored in localStorage
 * - Authenticated users: mode loaded from + persisted to backend (localStorage used as fallback)
 */
export function useSaxaMode() {
  const { user, isAuthenticated } = useAuth();
  const [mode, setModeState] = useState<AvatarMode>(readLocalMode);
  const [loaded, setLoaded] = useState(false);

  // Load mode: from API for logged-in users, from localStorage for guests
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setModeState(readLocalMode());
      setLoaded(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${API_ENDPOINTS.USER_PREFERENCES}?userId=${encodeURIComponent(user.id)}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && data.saxa_avatar_mode && VALID_MODES.includes(data.saxa_avatar_mode)) {
          setModeState(data.saxa_avatar_mode);
        }
      } catch (err) {
        console.error('[SaxaMode] Failed to load preference:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id]);

  const setMode = useCallback(
    (newMode: AvatarMode) => {
      if (!VALID_MODES.includes(newMode)) return;

      setModeState(newMode);

      // Always persist to localStorage (works for everyone)
      try { localStorage.setItem(STORAGE_KEY, newMode); } catch { /* ignore */ }

      // Also persist to backend for authenticated users
      if (isAuthenticated && user?.id) {
        fetch(API_ENDPOINTS.USER_PREFERENCES, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id, saxa_avatar_mode: newMode }),
        }).catch((err) => {
          console.error('[SaxaMode] Failed to save preference:', err);
        });
      }
    },
    [isAuthenticated, user?.id]
  );

  return {
    mode,
    setMode,
    loaded,
  };
}
